import React, {useState, useEffect, useContext, useMemo, useRef} from 'react';
import {FormattedMessage, injectIntl} from "react-intl";
import Text from "@amzn/meridian/text";
import Link from "@amzn/meridian/link";
import { InboundDataContext } from "./context/InboundDataContext";
import { InboundFilterContext } from "./context/InboundFilterContext";
import filterData from "./data/inboundFilterData.json";
import { transformStationPerformanceData } from "./utils/dataTransformer";
import { localStationTimeToEpochMs } from "./utils/dataTransformer";
import { getRefreshInterval } from "../util/Util";
import RefreshTimer from "../util/RefreshTimer";
import OperatingLines from "./OperatingLines";
import TrendChart from "./TrendChart";
import ChartExpandModal from "./ChartExpandModal";
import StowMetricsSection from "./StowMetricsSection";
import CubeAnalysisSection from "./CubeAnalysisSection";
import { INBOUND_STRINGS, TIME_SERIES_GRANULARITY_MINUTES, INBOUND_ACTION_TYPES, DEFAULT_TIME_RANGE_MS, FEATURES, CUBE_METRIC_NAMES } from "../util/Constants";
import { getFeaturesFromParent } from "../handler/ParentDataHandler";
import { fetchCubeMetrics } from "./network/InboundNetworkAPI";
import './Inbound.css';

const MetricCard = ({title, tooltip, children}) => {
    const [tipVisible, setTipVisible] = React.useState(false);

    return (
        <article className="metric-card">
            {tooltip && (
                <span
                    className="spec-tooltip"
                    onMouseEnter={function() { setTipVisible(true); }}
                    onMouseLeave={function() { setTipVisible(false); }}
                >
                    i
                    {tipVisible && (
                        <span className="spec-tooltip-content">{tooltip}</span>
                    )}
                </span>
            )}
            <Text size="sm" weight="regular" color="subdued" className="metric-card-title">
                {title}
            </Text>
            {children}
        </article>
    );
};

const StationPerformanceSummary = ({intl}) => {
    const { viewModel, dispatch } = useContext(InboundDataContext);
    const { filter } = useContext(InboundFilterContext);
    const [chartModalOpen, setChartModalOpen] = useState(false);
    const isCubeMetricsEnabled = useRef(false);

    // Transform API data into UI format — pass stationTimezone from filter context
    // (already fetched on page load via getFilters) instead of relying on PPH response
    const data = useMemo(() => {
        if (!viewModel.pphData || !viewModel.downtimeData) {
            return null;
        }
        return transformStationPerformanceData(
            viewModel.pphData,
            viewModel.downtimeData,
            filter.stationTimezone,
            viewModel.qualityData
        );
    }, [viewModel.pphData, viewModel.downtimeData, viewModel.qualityData, filter.stationTimezone]);

    const computeTimeRange = () => {
        let timeRange;

        if (filter.useCustomTimeRange && filter.customStartDate && filter.customEndDate) {
            const fixedStart = localStationTimeToEpochMs(
                filter.customStartDate,
                filter.customStartTime || '00:00',
                filter.stationTimezone
            );

            if (filter.useShiftEndMode) {
                const shiftEndMs = localStationTimeToEpochMs(
                    filter.customEndDate,
                    filter.customEndTime || '23:59',
                    filter.stationTimezone
                );
                timeRange = {
                    startTime: fixedStart,
                    endTime: Math.min(Date.now(), shiftEndMs)
                };
            } else {
                const fixedEnd = localStationTimeToEpochMs(
                    filter.customEndDate,
                    filter.customEndTime || '23:59',
                    filter.stationTimezone
                );
                timeRange = {
                    startTime: fixedStart,
                    endTime: fixedEnd
                };
            }
        } else if (filter.selectedTimeRange) {
            const preset = filterData.timeRanges.find(r => r.id === filter.selectedTimeRange);
            const minutes = preset ? preset.minutes : 360;
            timeRange = {
                startTime: Date.now() - minutes * 60 * 1000,
                endTime: Date.now()
            };
        } else {
            timeRange = {
                startTime: Date.now() - DEFAULT_TIME_RANGE_MS,
                endTime: Date.now()
            };
        }

        return timeRange;
    };

    // Fetch data on mount and at intervals
    const fetchData = () => {
        dispatch({
            type: INBOUND_ACTION_TYPES.LOAD_INBOUND_DATA,
            payload: {
                dispatcher: dispatch,
                timeRange: computeTimeRange(),
                timeSeriesGranularity: TIME_SERIES_GRANULARITY_MINUTES,
                isCubeMetricsEnabled: isCubeMetricsEnabled.current
            }
        });
    };

    // Resolve the Cube Analysis feature flag once on mount. Deliberately kept out of the
    // API layer so the caller owns the enable/disable decision, and held in a ref so the
    // refresh interval below always reads the current value without re-running its effect.
    useEffect(() => {
        getFeaturesFromParent([FEATURES.IS_CUBE_METRICS_ENABLED]).then(featureStatus => {
            const isEnabled = Boolean(featureStatus && featureStatus.data && featureStatus.data.isCubeMetricsEnabled);
            isCubeMetricsEnabled.current = isEnabled;
            if (!isEnabled) {
                dispatch({
                    type: INBOUND_ACTION_TYPES.RECEIVE_CUBE_METRICS,
                    data: null
                });
                return;
            }
            // The mount fetch fires before the flag resolves, so kick off the first cube
            // call here rather than leaving the section blank until the next poll.
            fetchCubeMetrics(dispatch, computeTimeRange(), CUBE_METRIC_NAMES);
        }).catch(() => {
            // Feature-flag lookup failed (e.g. transient getFeaturesFromParent/postMessage
            // error), so we can't confirm the feature is enabled. Treat the section as
            // absent (no error banner) and preserve any loaded data, rather than surfacing
            // a hard error to stations that may not have the feature. Genuine metrics-API
            // failures still dispatch CUBE_METRICS_FAILURE from the API layer.
            isCubeMetricsEnabled.current = false;
            dispatch({
                type: INBOUND_ACTION_TYPES.CUBE_METRICS_UNAVAILABLE
            });
        });
    }, []);

    useEffect(() => {
        fetchData();

        if (filter.useShiftEndMode && filter.customEndDate && filter.customEndTime) {
            const shiftEndMs = localStationTimeToEpochMs(
                filter.customEndDate, filter.customEndTime, filter.stationTimezone);
            const interval = setInterval(() => {
                if (document.hidden) {
                    return;
                }
                if (Date.now() >= shiftEndMs) {
                    clearInterval(interval);
                    return;
                }
                fetchData();
            }, getRefreshInterval());
            return () => clearInterval(interval);
        }

        if (!filter.useCustomTimeRange) {
            const interval = setInterval(() => {
                if (!document.hidden) {
                    fetchData();
                }
            }, getRefreshInterval());
            return () => clearInterval(interval);
        }
    }, [filter.triggerFetch]);

    // Show error state first
    if (viewModel.error) {
        return (
            <div className="station-performance-summary">
                <h2 className="station-performance-title">
                    <FormattedMessage id="inbound.station_performance_summary" defaultMessage="Station Performance Summary"/>
                </h2>
                <div className="error-message">
                    <FormattedMessage id="inbound.error_loading_data" defaultMessage="Error loading data"/>: {viewModel.error}
                </div>
            </div>
        );
    }

    // Show loading state only on initial load (no existing data yet)
    if (!data) {
        return (
            <div className="station-performance-summary">
                <h2 className="station-performance-title">
                    <FormattedMessage id="inbound.station_performance_summary" defaultMessage="Station Performance Summary"/>
                </h2>
                <div className="loading-message">
                    <FormattedMessage id="inbound.loading_message" defaultMessage="Loading station performance data..."/>
                </div>
            </div>
        );
    }

    const totalFPH = Math.round((data && data.totalFPH && data.totalFPH.value) || 0);
    const lostHours = parseFloat(((data && data.lostHours && data.lostHours.value) || 0).toFixed(2));
    const inductDTDPMO = Math.round((data && data.inductDTDPMO && data.inductDTDPMO.value) || 0);

    const topImpactedLines = ((data && data.lostHours && data.lostHours.topImpactedLines) || []).map(function(line) {
        return {
            name: (line && line.name) || INBOUND_STRINGS.UNKNOWN,
            value: parseFloat(((line && line.hours) || 0).toFixed(2)) + INBOUND_STRINGS.HOURS_SUFFIX
        };
    });

    const bottomFPH = ((data && data.totalFPH && data.totalFPH.bottomLines) || []).map(function(line) {
        const pph = (line && line.pph) || 0;
        const percentage = (line && line.percentage) != null ? line.percentage : 0;
        return {
            name: (line && line.name) || INBOUND_STRINGS.UNKNOWN,
            value: percentage + '% (' + pph.toLocaleString() + ' PPH)'
        };
    });

    const topVolumeImpact = ((data && data.inductDTDPMO && data.inductDTDPMO.topVolumeImpact) || []).map(function(line) {
        return {
            name: (line && line.name) || INBOUND_STRINGS.UNKNOWN,
            value: parseFloat(((line && line.dpmo) || 0).toFixed(3)).toLocaleString()
        };
    });

    return (
        <div className="station-performance-summary">
            <RefreshTimer deps={[viewModel.lastSuccessfulFetch]} />
            <h2 className="station-performance-title">
                <FormattedMessage id="inbound.station_performance_summary" defaultMessage="Station Performance Summary"/>
            </h2>

            <div className="performance-grid">
                {/* Total FPH Card */}
                <MetricCard title={<FormattedMessage id="inbound.total_fph" defaultMessage="INDUCT FPH"/>} tooltip={data && data.totalFPH && intl.formatMessage({id: data.totalFPH.tooltip})}>
                    <div className="card-metric-row">
                        <div>
                            <div className="card-metric metric-primary">
                                {totalFPH.toLocaleString()}
                            </div>
                            <div className="card-description">
                                <FormattedMessage id="inbound.packages_per_hour" defaultMessage="Packages Per Hour (All Lines)"/>
                            </div>
                        </div>
                        <div className="card-metric-divider"></div>
                        <div className="card-metric-secondary">
                            <div className="card-metric metric-primary">
                                {((data && data.totalFPH && data.totalFPH.totalPackages) || 0).toLocaleString()}
                            </div>
                            <div className="card-description">
                                <FormattedMessage id="inbound.total_packages" defaultMessage="Total Packages"/>
                            </div>
                        </div>
                    </div>
                    <div>
                        <span className="trend-chart-label">
                            {/* ToDo Uncomment this - This message will be displayed in further CRs with changes related to planned station FPH logic addition*/}
                            {/* <FormattedMessage id="inbound.trend_vs_hourly_goal" defaultMessage="Trend vs. Hourly Goal (Selected Time Range)"/> */}
                        </span>
                        <div className="mini-chart-wrapper" onClick={function() { setChartModalOpen(true); }}>
                            <div className="trend-chart-container">
                                <TrendChart trendData={(data && data.totalFPH && data.totalFPH.trendData) || []} />
                            </div>
                            <button className="expand-button" title="Expand chart">&#x2922;</button>
                        </div>
                    </div>
                    <div className="card-warning-wrapper">
                        <div className="card-divider"></div>
                        <div className="card-bottom-section">
                            <div className="card-section-title">
                                <FormattedMessage id="inbound.top_3_impacted_lines" defaultMessage="Top 3 Impacted Lines"/>
                            </div>
                            <div className="card-list-items">
                                {bottomFPH.map((item, index) => (
                                    <div key={index} className="card-list-item">
                                        <span className="card-list-item-name">{item.name}</span>
                                        <span className="card-list-item-value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="card-view-policy">
                                <div className="card-divider"></div>
                                <Link href={INBOUND_STRINGS.POLICY_URL_FPH} target="_blank" type="secondary">
                                    <FormattedMessage id="inbound.view_policy" defaultMessage="View Policy"/>
                                </Link>
                            </div>
                        </div>
                    </div>
                </MetricCard>

                {/* Lost Hours Card */}
                <MetricCard title={<FormattedMessage id="inbound.lost_hours" defaultMessage="LOST HOURS"/>} tooltip={data && data.lostHours && intl.formatMessage({id: data.lostHours.tooltip})}>
                    <div className="card-metric metric-primary">
                        {lostHours}h
                    </div>
                    <div className="card-description">
                        <FormattedMessage id="inbound.cumulative_downtime" defaultMessage="Cumulative Downtime"/>
                    </div>
                    <div className="card-warning-wrapper">
                        <div className="card-divider"></div>
                        <div className="card-bottom-section">
                            <div className="card-section-title">
                                <FormattedMessage id="inbound.top_3_impacted_lines" defaultMessage="Top 3 Impacted Lines"/>
                            </div>
                            <div className="card-list-items">
                                {topImpactedLines.map((item, index) => (
                                    <div key={index} className="card-list-item">
                                        <span className="card-list-item-name">{item.name}</span>
                                        <span className="card-list-item-value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="card-view-policy">
                                <div className="card-divider"></div>
                                <Link href={INBOUND_STRINGS.POLICY_URL_LOST_HOURS} target="_blank" type="secondary">
                                    <FormattedMessage id="inbound.view_policy" defaultMessage="View Policy"/>
                                </Link>
                            </div>
                        </div>
                    </div>
                </MetricCard>

                {/* Induct DT DPMO Card */}
                <MetricCard title={<FormattedMessage id="inbound.induct_dt_dpmo" defaultMessage="INDUCT DOWNTIME DPMO"/>} tooltip={data && data.inductDTDPMO && intl.formatMessage({id: data.inductDTDPMO.tooltip})}>
                    <div className="card-metric metric-primary">
                        {inductDTDPMO.toLocaleString()}
                    </div>
                    <div className="card-description">
                        <FormattedMessage id="inbound.defects_per_million" defaultMessage="Defects Per Million Opportunities"/>
                    </div>
                    <div className="card-warning-wrapper">
                        <div className="card-divider"></div>
                        <div className="card-bottom-section">
                            <div className="card-section-title">
                                <FormattedMessage id="inbound.top_3_volume_impact" defaultMessage="Top 3 by Volume Impact"/>
                            </div>
                            <div className="card-list-items">
                                {topVolumeImpact.map((item, index) => (
                                    <div key={index} className="card-list-item">
                                        <span className="card-list-item-name">{item.name}</span>
                                        <span className="card-list-item-value">{item.value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="card-view-policy">
                                <div className="card-divider"></div>
                                <Link href={INBOUND_STRINGS.POLICY_URL_DPMO} target="_blank" type="secondary">
                                    <FormattedMessage id="inbound.view_policy" defaultMessage="View Policy"/>
                                </Link>
                            </div>
                        </div>
                    </div>
                </MetricCard>

                {/* Quality Metrics Card */}
                <MetricCard title={<FormattedMessage id="inbound.quality_metrics" defaultMessage="QUALITY METRICS"/>} tooltip={data && data.qualityMetrics && data.qualityMetrics.isEnabled && intl.formatMessage({id: data.qualityMetrics.tooltip})}>
                    {data && data.qualityMetrics && data.qualityMetrics.isEnabled ? (
                        <React.Fragment>
                            <div className="quality-metrics-list">
                                {(data.qualityMetrics.metrics || []).map((metric, index) => (
                                    <div key={index} className="quality-metric-row">
                                        <span className="quality-metric-label">{intl.formatMessage({id: metric.messageId, defaultMessage: metric.label})}:</span>
                                        <span className={'quality-metric-value ' + (metric.status === 'critical' ? 'quality-value-negative' : metric.status === 'neutral' ? 'quality-value-neutral' : 'quality-value-positive')}>
                                            {metric.value !== null ? metric.value.toFixed(1) + '%' : '—'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="card-warning-wrapper">
                                <div className="card-divider"></div>
                                <div className="card-bottom-section">
                                    <div className="card-section-title">
                                        <FormattedMessage id="inbound.top_impact_by_quality" defaultMessage="Top Impact by Quality Issue"/>
                                    </div>
                                    <div className="card-list-items">
                                        {(data.qualityMetrics.topImpact || []).map((item, index) => (
                                            <div key={index} className="card-list-item">
                                                <span className="card-list-item-name">{item.issue}</span>
                                                <span className="card-list-item-value">{item.name} ({item.rate.toFixed(1)}%)</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="card-view-policy">
                                        <div className="card-divider"></div>
                                        <Link href={INBOUND_STRINGS.POLICY_URL_QUALITY} target="_blank" type="secondary">
                                            <FormattedMessage id="inbound.view_policy" defaultMessage="View Policy"/>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    ) : (
                        <div className="coming-soon-container">
                            <span className="coming-soon-text">
                                <FormattedMessage id="inbound.coming_soon" defaultMessage="Coming Soon"/>
                            </span>
                        </div>
                    )}
                </MetricCard>
            </div>

            {/* Stow Metrics Section — Cycle 1 */}
            <StowMetricsSection stowMetrics={viewModel.stowMetrics} stowMetricsError={viewModel.stowMetricsError} />

            {/* Cube Analysis Section — gated behind isCubeMetricsEnabled feature flag */}
            <CubeAnalysisSection cubeMetrics={viewModel.cubeMetrics} cubeMetricsError={viewModel.cubeMetricsError} />

            <OperatingLines linesData={(data && data.operatingLines) || []} />

            <ChartExpandModal
                open={chartModalOpen}
                onClose={function() { setChartModalOpen(false); }}
                title={<FormattedMessage id="inbound.fph_performance_detailed" defaultMessage="FPH Performance - Detailed View"/>}
                trendData={(data && data.totalFPH && data.totalFPH.trendData) || []}
                tableData={(data && data.totalFPH && data.totalFPH.trendData) || []}
            />
        </div>
    );
};

export default injectIntl(StationPerformanceSummary);



// WEBPACK FOOTER //
// ./src/components/inbound/StationPerformanceSummary.js